import type { CatalogIndex, Country, Place, Scope, PlaceType, GameConfig } from "../../shared/game.ts";
import { eligible } from "../../shared/game.ts";

let indexP: Promise<CatalogIndex> | null = null;
let placesP: Promise<Place[]> | null = null;

export function loadIndex(): Promise<CatalogIndex> {
  return (indexP ??= fetch("/catalog/index.json").then(r => { if (!r.ok) throw new Error("catalog index"); return r.json(); }));
}
export function loadPlaces(): Promise<Place[]> {
  return (placesP ??= fetch("/catalog/places.json").then(r => { if (!r.ok) throw new Error("catalog places"); return r.json(); }));
}

export interface Catalog {
  index: CatalogIndex;
  places: Place[];
  byCountry: Map<string, Country>;
  continentOf: Map<string, string>;
  regionName: Map<string, string>;
}

let cat: Catalog | null = null;
export async function catalog(): Promise<Catalog> {
  if (cat) return cat;
  const [index, places] = await Promise.all([loadIndex(), loadPlaces()]);
  const byCountry = new Map(index.countries.map(c => [c.code, c]));
  const continentOf = new Map(index.countries.map(c => [c.code, c.continent]));
  const regionName = new Map<string, string>();
  for (const c of index.countries) for (const r of c.regions) regionName.set(r.code, r.name);
  cat = { index, places, byCountry, continentOf, regionName };
  return cat;
}

export function scopeName(scope: Scope, c: Catalog): string {
  if (scope.kind === "world") return "The whole world";
  if (scope.kind === "continent") return scope.name;
  const country = c.byCountry.get(scope.code);
  const name = country?.name ?? scope.code;
  if (scope.regions?.length === 1) return `${c.regionName.get(scope.regions[0]) ?? ""}, ${name}`;
  if (scope.regions?.length) return `${name} (${scope.regions.length} ${regionWordPlural(country)})`;
  return name;
}

export function regionWordPlural(c?: Country) {
  const w = (c?.regionWord ?? "Region").toLowerCase();
  return w === "country" ? "countries" : w + "s";
}

export function countPlaces(cfg: GameConfig, c: Catalog): number {
  return eligible(c.places, { ...cfg, difficulty: "expert" }, c.continentOf).length;
}

/** "Capital of Kenya", "Capital of Texas", "City in Bavaria, Germany" … */
export function describe(p: Place, c: Catalog): string {
  const country = c.byCountry.get(p.c)?.name ?? p.c;
  const region = p.r ? c.regionName.get(p.r) : undefined;
  switch (p.t) {
    case "cap": return `Capital of ${country}`;
    case "rcap": return region ? `Capital of ${region}` : `Regional capital in ${country}`;
    case "mark": return region ? `${region}, ${country}` : country;
    default: return region && region !== p.n ? `${region}, ${country}` : country;
  }
}

/** A short line of real information for the reveal card, from the catalog data. */
export function aboutLine(p: Place, c: Catalog): string {
  const country = c.byCountry.get(p.c);
  const region = p.r ? c.regionName.get(p.r) : undefined;
  const pop = p.p > 0 ? `About ${formatPop(p.p)} people live in and around ${p.n}.` : "";
  switch (p.t) {
    case "cap": return `${p.n} is the capital of ${country?.name ?? p.c}, in ${country?.continent ?? "the world"}. ${pop}`.trim();
    case "rcap": return `${p.n} is the capital of ${region ?? "its region"}${country ? ` in ${country.name}` : ""}. ${pop}`.trim();
    case "mark": return `${p.n} is in ${region ?? ""}${region && country ? ", " : ""}${country?.name ?? ""}.`;
    default: return `${p.n} is a city in ${region ? region + ", " : ""}${country?.name ?? p.c}. ${pop}`.trim();
  }
}

export function formatPop(n: number): string {
  if (n >= 1e6) return `${(n / 1e6).toFixed(n >= 1e7 ? 0 : 1)} million`;
  if (n >= 1e3) return `${Math.round(n / 1e3).toLocaleString()} thousand`;
  return n.toLocaleString();
}

export const KIND_INFO: Record<PlaceType, { label: string; sub: (country: boolean) => string }> = {
  cap: { label: "Capitals", sub: w => (w ? "National capital" : "Every country’s capital") },
  rcap: { label: "State capitals", sub: () => "States & provinces" },
  city: { label: "Big cities", sub: w => (w ? "By population" : "Each country’s best known") },
  mark: { label: "Landmarks", sub: () => "Parks, rivers, monuments" },
};
